<script setup lang="ts">
import { computed } from 'vue'
import { CURRENCY_CODE } from '@shared/utils'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import { formatBand, formatVariance } from '../../trading'
import SellAmountField from './SellAmountField.vue'
import SellPriceBand from './SellPriceBand.vue'
import SellSummaryCard, { type SellSummaryRow, type SellRowTone } from './SellSummaryCard.vue'
import SellNote from './SellNote.vue'
import {
  GROSS_SALE_VALUE_SHEET,
  buildPermittedBandSheet,
  outsideBandNote,
} from '../constants/sellCopy'
import { formatAmount, formatTokenCount, sanitizePrice } from '../utils/sellListingUtils'

// Sell 4 / 5 — Set Price (Figma 34796:138685, outside band 34796:138744):
// price per token, the permitted band, and the figures that price produces.

interface Props {
  valuation: number
  bandMin: number
  bandMax: number
  /** Permitted band, ± % of valuation. */
  bandPct: number
  /** Parsed price; null while the field is empty or not a number. */
  price: number | null
  tokens: number
  variancePct: number | null
  grossValue: number
  isOutsideBand: boolean
  error: string | null
}

const props = defineProps<Props>()

const priceText = defineModel<string>('priceText', { required: true })

const emit = defineEmits<{ (e: 'blur'): void }>()

const hint = computed(() => `Permitted band ${formatBand(props.bandMin, props.bandMax)}`)

const bandSheet = computed(() =>
  buildPermittedBandSheet({
    valuation: props.valuation,
    bandMin: props.bandMin,
    bandMax: props.bandMax,
    bandPct: props.bandPct,
  }),
)

const bandNote = computed(() => outsideBandNote(props.bandPct))

// Below valuation is the buyer-friendly side, so it reads green; outside the
// band everything about the price reads red.
const varianceTone = computed<SellRowTone>(() => {
  if (props.isOutsideBand) return 'danger'
  return props.variancePct !== null && props.variancePct < 0 ? 'success' : 'default'
})

// A price outside the band can't be listed, so the quantity and sale value
// drop out (Figma 34796:138744).
const rows = computed<SellSummaryRow[]>(() => {
  const yourPrice: SellSummaryRow = {
    label: 'Your price',
    value: props.price === null ? '-' : formatAmount(props.price),
    tone: props.isOutsideBand ? 'danger' : 'default',
  }
  const variance: SellSummaryRow = {
    label: 'Variance vs valuation',
    value: props.variancePct === null ? '-' : formatVariance(props.variancePct),
    tone: varianceTone.value,
  }
  const valuation: SellSummaryRow = {
    label: 'Latest DLD valuation',
    value: formatAmount(props.valuation),
  }
  if (props.isOutsideBand) return [valuation, yourPrice, variance]
  return [
    valuation,
    yourPrice,
    { label: 'Tokens Listing', value: formatTokenCount(props.tokens) },
    variance,
    {
      label: 'Gross sale value',
      value: formatAmount(props.grossValue),
      info: GROSS_SALE_VALUE_SHEET,
      isTotal: true,
    },
  ]
})
</script>

<template>
  <Box direction="col" gap="4">
    <Text
      is="h3"
      type="caption"
      weight="bold"
      color="var(--p-sliceal-gray-400)"
      class="sell-price__eyebrow"
      >PRICE PER TOKEN</Text
    >

    <SellAmountField
      v-model="priceText"
      :sanitize="sanitizePrice"
      :prefix="CURRENCY_CODE"
      label="Price per token"
      inputmode="decimal"
      placeholder="0.00"
      described-by-id="sell-price-hint"
      :hint="hint"
      :error="error"
      @blur="emit('blur')"
    >
      <template #hint-append>
        <InfoSheetButton :content="bandSheet" label="About the permitted band" :size="14" />
      </template>
    </SellAmountField>

    <SellPriceBand
      :band-min="bandMin"
      :band-max="bandMax"
      :valuation="valuation"
      :price="price"
      :is-outside="isOutsideBand"
    />

    <SellSummaryCard :rows="rows" />

    <SellNote v-if="isOutsideBand" tone="danger" :title="bandNote.title" :text="bandNote.text" />
  </Box>
</template>

<style scoped>
.sell-price__eyebrow {
  margin: 0;
  line-height: 1.4;
}
</style>
